import { useState, useRef, useEffect, AllHTMLAttributes } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  shift,
  useDismiss,
  useInteractions,
  arrow,
  Placement,
} from '@floating-ui/react';

// this is essentially an interactive tooltip, a tooltip that opens on click and has a handle close

type UseToggletipOptions = {
  placement: Placement;
  isOpenState: boolean;
  setIsOpenState: (isOpen: boolean) => void;
  localStorageKey?: string;
};

export const useToggletip = ({
  placement,
  isOpenState,
  setIsOpenState,
  localStorageKey,
}: UseToggletipOptions) => {
  const [isTooltipClosed, setIsTooltipClosed] = useState(false);
  const arrowRef = useRef(null);

  // UseEffect to handle localStorage for each tooltip individually
  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const closedState =
        localStorageKey && localStorage.getItem(localStorageKey);
      setIsTooltipClosed(closedState === 'true');
    } else {
      setIsTooltipClosed(false); // Fallback for environments without localStorage
    }
  }, [localStorageKey]);

  const handleClose = () => {
    setIsOpenState(false);
    // Set the localStorage value to prevent showing the tooltip again
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorageKey && localStorage.setItem(localStorageKey, 'true');
    }
  };

  const { refs, floatingStyles, context } = useFloating({
    open: isOpenState && !isTooltipClosed,
    onOpenChange: setIsOpenState,
    placement,
    strategy: 'absolute',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      shift(),
      arrow({
        element: arrowRef,
      }),
    ],
  });

  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

  return {
    getTargetProps: (userOverrides: AllHTMLAttributes<HTMLElement> = {}) => {
      return {
        ...getReferenceProps({
          ...userOverrides,
          onClick: () => setIsOpenState(!isOpenState),
        }),
        ref: refs.setReference,
      };
    },
    getToggletipProps: (userOverrides: AllHTMLAttributes<HTMLElement> = {}) => {
      return {
        ...getFloatingProps({
          ...userOverrides,
          style: {
            ...floatingStyles,
            ...userOverrides.style,
          },
        }),
        ref: refs.setFloating,
        arrowRef,
        context,
        handleClose,
      };
    },
  };
};
